import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Col, Form, Row, Toast, ToastContainer } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import Select from 'react-select'
import {
  locationListApi,
  productGroupListApi,
  getBuyerWiseInquiryFinalPrice,
} from './_request'
import { checkDisplayValue } from '../../utils/utils'
import { KTCardBody } from '../../../_metronic/helpers'
import { OffersListLoading } from '../offer-management/offers-list/components/loading/OffersListLoading'
import { checkPermission } from '../../utils/checkPermission'

const BuyerWiseInquiryPrice = () => {
  const [sessionMessage, setSessionMessage] = useState({
    type: '',
    message: '',
    show: false,
  })
  const [locationList, setLocationList] = useState<any>([])
  const [selectedLocation, setSelectedLocation] = useState<any>(null)
  const [productGroupList, setProductGroupList] = useState<any>([])
  const [selectedProductGroup, setSelectedProductGroup] = useState<any>(null)
  const [offerPrice, setOfferPrice] = useState<any>([])
  const [buyerDetail, setBuyerDetail] = useState<any>(null)

  const offerValidation = Yup.object().shape({
    mobile_number: Yup.string()
      .required('Mobile Number is required')
      .matches(/^[6-9]\d{9}$/, 'Please enter a valid 10-digit mobile number'),
    product_group_id: Yup.string().required('Product Group is required'),
    destination_location_id: Yup.string().required('Location is required'),
    quantity: Yup.string()
      .required('Quantity is required')
      .test('positive', 'Quantity must be greater than 0', (v: any) => Number(v) > 0),
  })

  const formik = useFormik({
    initialValues: {
      mobile_number: '',
      product_group_id: '',
      destination_location_id: '',
      quantity: '25',
    },
    validationSchema: offerValidation,
    onSubmit: async (values: any) => {
      setOfferPrice([])
      setBuyerDetail(null)
      try {
        const data = await getBuyerWiseInquiryFinalPrice({
          mobile_number: values.mobile_number,
          product_group_id: values.product_group_id,
          destination_location_id: values.destination_location_id,
          qty: values.quantity,
        })
        if (data?.responseStatus == 200 && data?.responseData) {
          setBuyerDetail(data.responseData.buyer ?? null)
          setOfferPrice(data.responseData.list ?? [])
        } else {
          let message = data?.responseMessage ? data.responseMessage : 'Something went wrong'
          setSessionMessage({ type: 'error', message: message, show: true })
        }
      } catch (err: any) {
        let message = err?.message
          ? err.message
          : err?.responseMessage
            ? err.responseMessage
            : ('Something went wrong' as any)
        setSessionMessage({ type: 'error', message: message, show: true })
      }
    },
  })

  useEffect(() => {
    if (sessionMessage.show) {
      setTimeout(() => {
        setSessionMessage({
          type: '',
          message: '',
          show: false,
        })
      }, 5000)
    }
  }, [sessionMessage.message])

  const getLocationList = async (search: any) => {
    try {
      let { responseStatus, responseData, responseMessage } = (await locationListApi(search)) as any
      if (responseStatus == 200 && responseData) {
        setLocationList(
          responseData.map((x: any) => {
            x.label = x.name
            x.value = x.id
            return x
          })
        )
      } else {
        let message = responseMessage ? responseMessage : 'Something went wrong'
        setSessionMessage({ type: 'error', message: message, show: true })
      }
    } catch (err: any) {
      let message = err?.message
        ? err.message
        : err?.responseMessage
          ? err.responseMessage
          : ('Something went wrong' as any)
      setSessionMessage({ type: 'error', message: message, show: true })
    }
  }

  const getProductGroupList = async () => {
    try {
      let { responseStatus, responseMessage, data } = (await productGroupListApi()) as any
      if (responseStatus == 200 && data) {
        setProductGroupList(data.map((x: any) => ({ ...x, label: x.name, value: x.id })))
      } else {
        let message = responseMessage ? responseMessage : 'Something went wrong'
        setSessionMessage({ type: 'error', message: message, show: true })
      }
    } catch (err: any) {
      let message = err?.message ? err.message : ('Something went wrong' as any)
      setSessionMessage({ type: 'error', message: message, show: true })
    }
  }

  // keep the selected location in the options when a search replaces the list
  const locationOptions =
    selectedLocation && !locationList.find((x: any) => x.value === selectedLocation.value)
      ? [...locationList, selectedLocation]
      : locationList

  useEffect(() => {
    getLocationList('')
    getProductGroupList()
  }, [])

  return (
    <>
      {checkPermission('Buyer Wise Inquiry Price', 'list') ? (
        <>
          <div className='d-flex justify-content-between align-items-center'>
            <div className='fw-bold fs-2'>Buyer Wise Inquiry Final Price</div>
          </div>
          <div className='card card-body p-12 mt-10'>
            <div className='offer-form-wrapper'>
              <div className='offer-form-card'>
                <form className='offer-form' onSubmit={formik.handleSubmit} noValidate>
                  <Row className='align-items-start mb-0' style={{ position: 'relative', zIndex: 99 }}>
                    <Col xs={12} md={3} className='mb-3 mb-md-0'>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>Mobile Number</Form.Label>
                        <Form.Control
                          type='text'
                          className='form-control-solid'
                          placeholder='Enter Buyer Mobile Number'
                          maxLength={10}
                          value={formik.values.mobile_number}
                          onBlur={() => formik.setFieldTouched('mobile_number', true)}
                          onChange={(e) =>
                            formik.setFieldValue('mobile_number', e.target.value.replace(/\D/g, ''))
                          }
                        />
                        {formik.touched.mobile_number && typeof formik.errors.mobile_number === 'string' && (
                          <div className='text-danger'>{formik.errors.mobile_number}</div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={3} className='mb-3 mb-md-0'>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>Product Group</Form.Label>
                        <Select
                          options={productGroupList}
                          className='custom_select zIndex-99'
                          classNamePrefix='Select'
                          placeholder='Select Product Group'
                          value={selectedProductGroup}
                          onBlur={() => formik.setFieldTouched('product_group_id', true)}
                          onChange={(e: any) => {
                            formik.setFieldValue('product_group_id', e?.value ?? '')
                            setSelectedProductGroup(e)
                          }}
                          styles={{
                            menu: (provided: any) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                        {formik.touched.product_group_id && typeof formik.errors.product_group_id === 'string' && (
                          <div className='text-danger'>{formik.errors.product_group_id}</div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={3} className='mb-3 mb-md-0'>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>Location</Form.Label>
                        <Select
                          options={locationOptions}
                          className='custom_select zIndex-99'
                          classNamePrefix='Select'
                          placeholder='Select Location'
                          value={selectedLocation}
                          onInputChange={(search: string, meta: any) => {
                            if (meta.action === 'input-change') getLocationList(search)
                          }}
                          onBlur={() => formik.setFieldTouched('destination_location_id', true)}
                          onChange={(e: any) => {
                            formik.setFieldValue('destination_location_id', e?.value ?? '')
                            setSelectedLocation(e)
                          }}
                          styles={{
                            menu: (provided: any) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                        {formik.touched.destination_location_id &&
                          typeof formik.errors.destination_location_id === 'string' && (
                            <div className='text-danger'>{formik.errors.destination_location_id}</div>
                          )}
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={3}>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>Quantity</Form.Label>
                        <Form.Control
                          type='text'
                          className='form-control-solid'
                          placeholder='Enter Quantity'
                          value={formik.values.quantity}
                          onBlur={() => formik.setFieldTouched('quantity', true)}
                          onChange={(e) => formik.setFieldValue('quantity', e.target.value.trimStart())}
                        />
                        {formik.touched.quantity && typeof formik.errors.quantity === 'string' && (
                          <div className='text-danger'>{formik.errors.quantity}</div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className='justify-content-center mt-3'>
                    <Col xs={12} md={3} className='d-flex justify-content-center'>
                      <button type='submit' className='btn btn-primary offer-form-btn' disabled={formik.isSubmitting}>
                        {formik.isSubmitting ? 'Please Wait...' : 'Price'}
                      </button>
                    </Col>
                  </Row>
                </form>
              </div>
            </div>
            <ToastContainer className='p-3' position={'top-end'}>
              <Toast
                show={sessionMessage.show}
                onClose={() => {
                  setSessionMessage({
                    type: '',
                    message: '',
                    show: false,
                  })
                }}
                bg='danger'
              >
                <Toast.Body className='text-white'>{sessionMessage.message}</Toast.Body>
              </Toast>
            </ToastContainer>
            <KTCardBody className='py-4'>
              {buyerDetail && (
                <div className='mb-4 text fs-6'>
                  Inquiry final price for buyer
                  <span className='fw-bold text-primary ms-1'>{buyerDetail.firm_name}</span>
                  {buyerDetail.person_name && (
                    <span className='ms-1'>
                      ({buyerDetail.person_name} &middot; {buyerDetail.mobile_number})
                    </span>
                  )}
                </div>
              )}
              <div className='offer-with-price-table-container' style={{ position: 'relative', zIndex: 1 }}>
                <table
                  id='kt_table_users'
                  className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer offer-with-price-table'
                >
                  <thead>
                    <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0 bg-light'>
                      <th style={{ width: '25%' }}>Product Variation</th>
                      <th style={{ width: '25%' }}>Seller Firm Name</th>
                      <th style={{ width: '15%' }}>Offer Number</th>
                      <th style={{ width: '15%' }}>Offer Price</th>
                      <th style={{ width: '20%' }}>Inquiry Final Price</th>
                    </tr>
                  </thead>
                  <tbody className='text-gray-600 fw-bold'>
                    {offerPrice && offerPrice.length > 0 ? (
                      offerPrice.map((row: any, index: any) => (
                        <tr key={row?.offer_id ?? index}>
                          <td>{checkDisplayValue(row?.product_variation)}</td>
                          <td>{checkDisplayValue(row?.seller_firm_name)}</td>
                          <td>{checkDisplayValue(row?.offer_number)}</td>
                          <td>{checkDisplayValue(row?.offer_price)}</td>
                          <td className='text-primary fw-bolder'>{checkDisplayValue(row?.inquiry_final_price)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                            No Offers Found
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {formik.isSubmitting && <OffersListLoading />}
            </KTCardBody>
          </div>
        </>
      ) : (
        ''
      )}
    </>
  )
}
export default BuyerWiseInquiryPrice
